import { styled } from "@linaria/react";
import React from "react";
import type { FormatOptionLabelMeta } from "react-select/base";
import Ellipsis from "../Ellipsis";
import Tick from "../Tick";
import classNames from "classnames";
import t from "../i18n";
import ButtonIconV2 from "../ButtonIconV2";
import { DeleteBinLine, PencilLine } from "../IconV2/react";
import { color } from "..";

export interface BaseOption {
  label: string;
  value: string | null;
  disabled?: boolean;
  color?: string;
}

export const emptyOption: BaseOption = { label: t("react_select.no_value"), value: null };

const SELECTED_CN = "selected";

interface P<Option> {
  onEdit?: (option: Option, label: string) => void | Promise<void>;
  onDelete?: (option: Option) => Promise<void>;
  getOptionLabel: (option: Option) => string;
}

const StyledTick = styled(Tick)``;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 2px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 8px;

  :not(.${SELECTED_CN}) ${StyledTick} {
    visibility: hidden;
  }

  :not(:hover) {
    ${Actions} {
      display: none;
    }
  }
`;

const EditButton = styled(ButtonIconV2)`
  color: currentColor;
`;

const DeleteButton = styled(ButtonIconV2)`
  color: currentColor;
`;

function useFormatOptionLabel<Option extends BaseOption & { __isNew__?: boolean }>({
  onDelete,
  onEdit,
  getOptionLabel,
}: P<Option>) {
  const [loading, setLoading] = React.useState(false);
  return React.useCallback(
    (option: Option, formatOptionLabelMeta: FormatOptionLabelMeta<Option>) => {
      const disableOption = option.value === emptyOption.value || option.__isNew__;
      const label = getOptionLabel(option);
      if (formatOptionLabelMeta.context === "value") return label;
      const { selectValue } = formatOptionLabelMeta;
      const selected = Boolean(selectValue.find(sv => sv.value === option.value));
      return (
        <Wrapper className={classNames({ [SELECTED_CN]: selected })}>
          <StyledTick />
          <Ellipsis>{label}</Ellipsis>
          {(onDelete || onEdit) && !disableOption ? (
            <Actions>
              {onEdit ? (
                <EditButton
                  disabled={loading || option.disabled}
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      // eslint-disable-next-line no-alert
                      const newLabel = window.prompt(t("react_select.confirm_edit", label), label);
                      if (newLabel) {
                        setLoading(true);
                        await onEdit(option, newLabel);
                      }
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <PencilLine color={color.carbon800} />
                </EditButton>
              ) : null}
              {onDelete ? (
                <DeleteButton
                  disabled={loading || option.disabled}
                  onClick={async e => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      setLoading(true);
                      // eslint-disable-next-line no-alert
                      if (window.confirm(t("react_select.confirm_delete", label))) {
                        await onDelete(option);
                      }
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <DeleteBinLine color={color.carbon800} />
                </DeleteButton>
              ) : null}
            </Actions>
          ) : null}
        </Wrapper>
      );
    },
    [getOptionLabel, loading, onDelete, onEdit],
  );
}

export default useFormatOptionLabel;
